import type { MemoryStore } from '@ownyou/memory-store';
import { isQaToolsEnabled } from '../wallet/qa-fixtures';
import type { DataSourceId } from '../../contexts/DataSourceContext';
import slice08Contract from './slice08-hitl-contract.json';
import { writeSlice08SandboxSourceEvidence } from './slice08-hitl-store-ops';

export const SLICE08_SANDBOX_CONTRACT = slice08Contract.sandbox;

export interface Slice08SandboxIdentity {
  walletAddress: string;
  userId: string;
}

export interface Slice08SandboxEvidenceResult {
  ok: boolean;
  sourceId?: DataSourceId;
  reasonCode?: string;
}

export interface Slice08SandboxController {
  writeSourceEvidence: (input: { sourceId: DataSourceId }) => Promise<Slice08SandboxEvidenceResult>;
}

declare global {
  interface Window {
    __OWNYOU_SLICE08_SANDBOX__?: Slice08SandboxController;
  }
}

function browserSessionStorage(): Storage | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage;
}

export function isSlice08SandboxIdentityReady(storage: Storage | null = browserSessionStorage()): boolean {
  return Boolean(
    isQaToolsEnabled()
    && storage
    && storage.getItem(SLICE08_SANDBOX_CONTRACT.identityKey) === SLICE08_SANDBOX_CONTRACT.identityReadyValue,
  );
}

export function getSlice08SandboxIdentity(storage?: Storage | null): Slice08SandboxIdentity | null {
  if (!isSlice08SandboxIdentityReady(storage ?? browserSessionStorage())) return null;
  return {
    walletAddress: SLICE08_SANDBOX_CONTRACT.walletAddress,
    userId: SLICE08_SANDBOX_CONTRACT.walletAddress,
  };
}

export function isSlice08SandboxOAuthReturn(reason: string | null | undefined): boolean {
  return reason === SLICE08_SANDBOX_CONTRACT.callbackReason && isSlice08SandboxIdentityReady();
}

export async function writeSlice08SandboxWelcomeSourceEvidence(input: {
  store: MemoryStore;
  sourceId: DataSourceId;
  identity?: Slice08SandboxIdentity | null;
}): Promise<Slice08SandboxEvidenceResult> {
  const identity = input.identity ?? getSlice08SandboxIdentity();
  if (!identity) {
    return { ok: false, sourceId: input.sourceId, reasonCode: 'slice08-sandbox-identity-not-ready' };
  }

  await writeSlice08SandboxSourceEvidence({
    store: input.store,
    userId: identity.userId,
    sourceId: input.sourceId,
  });
  return { ok: true, sourceId: input.sourceId, reasonCode: 'slice08-sandbox-source-evidence-written' };
}

export function installSlice08SandboxQaWriter(store: MemoryStore): void {
  if (typeof window === 'undefined') return;

  if (!isSlice08SandboxIdentityReady()) {
    if (window.__OWNYOU_SLICE08_SANDBOX__) {
      delete window.__OWNYOU_SLICE08_SANDBOX__;
    }
    return;
  }

  window.__OWNYOU_SLICE08_SANDBOX__ = {
    writeSourceEvidence: ({ sourceId }) => writeSlice08SandboxWelcomeSourceEvidence({ store, sourceId }),
  };
}
